import React, { useMemo, useState } from "react";
import NavigationBtns from "./Navigation-btns";
import SimpleReactLightbox, { SRLWrapper } from "simple-react-lightbox";

const CHAT_INDEX_TEMPLATES = [
  {
    label: "Index 1",
    description:
      "Required to load chat messages in ascending order by the time field.",
    url: "https://console.firebase.google.com/v1/r/project/test-project-id/firestore/indexes?create_composite=Cldwcm9qZWN0cy90ZXN0LXByb2plY3QtaWQvZGF0YWJhc2VzLyhkZWZhdWx0KS9jb2xsZWN0aW9uR3JvdXBzL2NoYXQvaW5kZXhlcy9DSUNBZ0ppVXBvTUsQARoHCgNpZHMYARoICgR0aW1lEAEaDAoIX19uYW1lX18QAQ",
  },
  {
    label: "Index 2",
    description:
      "Required to load chat messages in descending order by the time field.",
    url: "https://console.firebase.google.com/v1/r/project/test-project-id/firestore/indexes?create_composite=Ckxwcm9qZWN0cy90ZXN0LXByb2plY3QtaWQvZGF0YWJhc2VzLyhkZWZhdWx0KS9jb2xsZWN0aW9uR3JvdXBzL2NoYXQvaW5kZXhlcy9fEAEaBwoDaWRzGAEaCAoEdGltZRACGgwKCF9fbmFtZV9fEAI",
  },
];

const TEMPLATE_PROJECT_ID = "test-project-id";

function buildChatIndexUrl(template, projectId) {
  if (!projectId) return template;
  let url = template.replace(
    `/project/${TEMPLATE_PROJECT_ID}/`,
    `/project/${projectId}/`
  );
  const match = url.match(/create_composite=([^&]+)/);
  if (!match) return url;
  try {
    const b64 = match[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(padded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);

    const targetBytes = new TextEncoder().encode(TEMPLATE_PROJECT_ID);
    let pos = -1;
    outer: for (let i = 0; i <= bytes.length - targetBytes.length; i++) {
      for (let j = 0; j < targetBytes.length; j++) {
        if (bytes[i + j] !== targetBytes[j]) continue outer;
      }
      pos = i;
      break;
    }
    if (pos === -1) return url;

    const newIdBytes = new TextEncoder().encode(projectId);
    const merged = new Uint8Array(
      bytes.length - targetBytes.length + newIdBytes.length
    );
    merged.set(bytes.subarray(0, pos), 0);
    merged.set(newIdBytes, pos);
    merged.set(
      bytes.subarray(pos + targetBytes.length),
      pos + newIdBytes.length
    );

    // Fix the length-prefix varint of the path field (proto field 1, tag 0x0A).
    if (merged[0] === 0x0a) {
      const newLen = merged[1] + (newIdBytes.length - targetBytes.length);
      if (newLen >= 0 && newLen < 128) {
        merged[1] = newLen;
      } else {
        return url;
      }
    }

    let newBin = "";
    for (let i = 0; i < merged.length; i++) {
      newBin += String.fromCharCode(merged[i]);
    }
    const newB64 = btoa(newBin)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    return url.replace(/create_composite=[^&]+/, `create_composite=${newB64}`);
  } catch {
    return url;
  }
}

const WebFirebaseSetup = () => {
  const [projectId, setProjectId] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);

  const chatIndexUrls = useMemo(
    () =>
      CHAT_INDEX_TEMPLATES.map((t) => ({
        ...t,
        finalUrl: buildChatIndexUrl(t.url, projectId.trim()),
      })),
    [projectId]
  );

  const copyToClipBoard = async (value, id) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedIndex(id);
    } catch {
      setCopiedIndex("error");
    }
  };

  return (
    <div
      className="introduction "
      data-aos="fade-right"
      data-aos-delay="300"
      data-aos-duration="1000"
    >
      <h1 className="title">Firebase</h1>
      <p className="inner-text">
        We use Firebase Authentication, Cloud Messaging and Firestore in
        GoShops. So, you have to setup Firebase in order to use GoShops
        properly.
      </p>
      <h4 className="mt-4">Web firebase configuration</h4>
      <div className="mt-4" />
      <div className="iframe-wrapper">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/OLwNp_e5bxM"
          title="Firebase configuration"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
      <div className="mt-4" />
      <div className="iframe-wrapper">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/jCgZZiz1480"
          title="How to run connect firebase to project"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>

      <div className="mt-4" />
      <div className="iframe-wrapper">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/5HzrGiY9cFo"
          title="Firebase auth configuration"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
      <div className="mt-4" />
      <div className="iframe-wrapper">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/5HzrGiY9cFo"
          title="Firebase auth configuration"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
      <div className="mt-4" />
      <div className="iframe-wrapper">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/cchc8T1QnCE?si=RvYlapwJ2S-oMd0h"
          title="Cloud messaging"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
      <div className="">
        <p style={{ margin: "10px" }}>
          Add downloaded file to backend_folder/storage/app and run the command
          php artisan storage:link if you haven’t run it before
        </p>
        <img
          src={`../assets/img/google-service-json.jpg`}
          alt="icon"
          style={{ width: "100%" }}
        />
      </div>
      <div className="mt-4" />
      <div className="iframe-wrapper">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/29ARDVIXvXk"
          title="Firestore"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
      <div className="mt-4" />
      <h2 className="title">Google maps</h2>
      <p className="inner-text">
        In this project we integrated Google Maps. You have to setup Google Maps
      </p>
      <p>
        Open &nbsp;
        <a
          href="https://console.cloud.google.com/"
          target="_blank"
          rel="noreferrer"
        >
          <u>Google Cloud Console</u>
        </a>
      </p>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a href="/assets/img/map_setup1.png" data-fancybox rel="nofollow">
              <img
                src="/assets/img/map_setup1.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a href="/assets/img/map_setup2.png" data-fancybox rel="nofollow">
              <img
                src="/assets/img/map_setup2.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a href="/assets/img/map_setup3.png" data-fancybox rel="nofollow">
              <img
                src="/assets/img/map_setup3.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a href="/assets/img/map_setup4.png" data-fancybox rel="nofollow">
              <img
                src="/assets/img/map_setup4.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a href="/assets/img/map_setup5.png" data-fancybox rel="nofollow">
              <img
                src="/assets/img/map_setup5.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a href="/assets/img/map_setup6.png" data-fancybox rel="nofollow">
              <img
                src="/assets/img/map_setup6.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <h2 className="title">Google Translate</h2>
      <p className="inner-text">
        The Google Translate integration is <strong>optional</strong>. Quick
        uses it to auto-fill product titles and descriptions across locales
        in the product create/edit form. If you don&apos;t need
        auto-translation, you can skip this section entirely. To enable it,
        follow the steps below: turn on the Cloud Translation API in Google
        Cloud Console, generate an API key, and paste it into the admin
        panel.
      </p>
      <p>
        Open &nbsp;
        <a
          href="https://console.cloud.google.com/apis/library"
          target="_blank"
          rel="noreferrer"
        >
          <u>Google Cloud Console &mdash; API Library</u>
        </a>
        &nbsp; and sign in with the Google account that owns your project. If
        you don&apos;t have a project yet, create one from the project picker
        in the top bar before continuing.
      </p>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/translate_setup1.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/translate_setup1.png"
                alt="Google Cloud Console — API Library"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <p style={{ marginBottom: 0 }}>
        1. In the API Library search bar, type{" "}
        <strong>Cloud Translation API</strong> and open the matching result.
      </p>
      <p style={{ marginBottom: 0 }}>
        2. Click the blue <strong>Enable</strong> button. If billing is not yet
        enabled for the project, Google will prompt you to link a billing
        account &mdash; the Cloud Translation API requires billing to be on,
        even though Google offers a generous free monthly quota.
      </p>
      <p style={{ marginBottom: 0 }}>
        3. Once the API is enabled, open the left-hand menu and go to{" "}
        <strong>APIs &amp; Services &rarr; Credentials</strong>.
      </p>
      <p>
        4. Click <strong>+ Create credentials</strong> at the top of the page
        and choose <strong>API key</strong>. A dialog will appear with the
        newly created key &mdash; copy it.
      </p>
      <p>
        Now switch to your <strong>admin panel</strong> and open{" "}
        <strong>General settings</strong> &rarr;{" "}
        <strong>Google translate</strong>.
      </p>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/translate_setup2.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/translate_setup2.png"
                alt="Admin panel — General settings — Google translate"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <p style={{ marginBottom: 0 }}>
        1. Paste the API key you just copied into the{" "}
        <strong>API key</strong> input.
      </p>
      <p style={{ marginBottom: 0 }}>
        2. Toggle the <strong>Google translate</strong> switch on to activate
        the feature.
      </p>
      <p>
        3. Click <strong>Save</strong>. The storefront picks up the new
        settings on the next request &mdash; no rebuild required. The
        per-field translate button and the on-publish auto-translate flow in
        the product create/edit form will start working immediately.
      </p>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <h2 className="title">Google recaptcha</h2>
      <p className="inner-text">
        Google recaptcha is used in admin panel's login page.
      </p>
      <p>
        Open &nbsp;
        <a
          href="https://www.google.com/u/1/recaptcha/admin/create"
          target="_blank"
          rel="noreferrer"
        >
          <u>Google recaptcha admin</u>
        </a>
      </p>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/recaptcha_step1.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/recaptcha_step1.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/recaptcha_step2.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/recaptcha_step2.png"
                alt="admin"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <h2 className="title">Firebase configuration in the admin panel</h2>
      <p className="inner-text">
        The same Firebase web SDK credentials you placed in the
        storefront&apos;s <code>.env</code> must also be entered in the admin
        panel so that the backend can deliver push notifications and chat events
        to web clients. This is a separate, one-time step performed from the
        admin UI.
      </p>
      <p style={{ marginBottom: 0 }}>
        1. Sign in to the <strong>admin panel</strong>.
      </p>
      <p style={{ marginBottom: 0 }}>
        2. Open <strong>Notification settings</strong> &rarr;{" "}
        <strong>Firebase configuration</strong>.
      </p>
      <p style={{ marginBottom: 0 }}>
        3. Fill in every field with the matching value from your Firebase
        project (<code>apiKey</code>, <code>authDomain</code>,{" "}
        <code>projectId</code>, <code>storageBucket</code>,{" "}
        <code>messagingSenderId</code>, <code>appId</code>,{" "}
        <code>measurementId</code>, <code>vapidKey</code>).
      </p>
      <p>
        4. Save the form. The values must match the ones in the
        storefront&apos;s <code>.env</code> and{" "}
        <code>public/firebase-messaging-sw.js</code> exactly &mdash; any
        mismatch will break push notifications.
      </p>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/doc/quick-admin-firebase-config.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/doc/quick-admin-firebase-config.png"
                alt="Admin panel — Notification settings — Firebase configuration"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <h2 className="title">Authorized domains for Google sign-in</h2>
      <p className="inner-text">
        Firebase Authentication only allows the OAuth sign-in flow (Google,
        Facebook, etc.) to run from domains explicitly added to the{" "}
        <strong>Authorized domains</strong> list of your project. If your
        production domain is missing, the Google sign-in popup will fail with an{" "}
        <em>auth/unauthorized-domain</em> error.
      </p>
      <p>
        Open the{" "}
        <a
          href="https://console.firebase.google.com/"
          target="_blank"
          rel="noreferrer"
        >
          Firebase Console
        </a>{" "}
        and follow the steps below to add your website&apos;s domain:
      </p>
      <p style={{ marginBottom: 0 }}>
        1. Select your project &rarr; <strong>Security</strong> &rarr;{" "}
        <strong>Authentication</strong>.
      </p>
      <p style={{ marginBottom: 0 }}>
        2. Switch to the <strong>Settings</strong> tab and open{" "}
        <strong>Authorized domains</strong>.
      </p>
      <p style={{ marginBottom: 0 }}>
        3. Click <strong>Add domain</strong> and enter your production hostname
        (e.g. <code>quick-web.githubit.com</code>). Do not include the protocol
        or a trailing slash.
      </p>
      <p>
        4. Repeat for every additional domain that should serve the site
        (staging, preview deployments, custom subdomains).
      </p>
      <div className="alert alert-primary mt-3" role="alert">
        <strong>Note:</strong> <code>localhost</code> is authorized by default,
        so local development works out of the box. You only need to add real
        domains used in production or staging.
      </div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/doc/quick-authorized-domains.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/doc/quick-authorized-domains.png"
                alt="Firebase Authentication authorized domains"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <h2 className="title">Firestore chat indexes</h2>
      <p className="inner-text">
        Quick&apos;s in-app chat queries Firestore with sorted queries that
        require composite indexes. Without these indexes, the chat will fail to
        load message history and the browser console will surface a{" "}
        <em>FAILED_PRECONDITION</em> error from Firestore.
      </p>
      <p>
        Enter your Firebase project ID below to generate the index-creation
        links. Open each link, sign in to the Firebase console, and click{" "}
        <strong>Create index</strong>. The first build can take a few minutes to
        complete.
      </p>
      <div className="form-group mt-3" style={{ maxWidth: "480px" }}>
        <label htmlFor="firebase-project-id">
          <strong>Your Firebase project ID</strong>
        </label>
        <input
          id="firebase-project-id"
          type="text"
          className="form-control mt-2"
          placeholder="e.g. quick-prod-1234"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {chatIndexUrls.map((item, idx) => (
        <div className="mt-4" key={item.label}>
          <strong>{item.label}</strong> &mdash; {item.description}
          <div className="introduction-code">
            <div>
              <span
                className="ps-0"
                style={{ wordBreak: "break-all", paddingRight: "1rem" }}
              >
                {item.finalUrl}
              </span>
              <span
                className={copiedIndex === idx ? "bg-success copy" : "copy"}
                onClick={() => copyToClipBoard(item.finalUrl, idx)}
              >
                {copiedIndex === idx ? "copied!" : "copy"}
              </span>
            </div>
          </div>
          <a
            href={item.finalUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 d-inline-block"
          >
            Open in Firebase console &rarr;
          </a>
        </div>
      ))}
      <div className="ptf-spacer" style={{ "--ptf-xxl": "3.75rem" }}></div>
      <NavigationBtns />
    </div>
  );
};

export default WebFirebaseSetup;
